import classNames from 'classnames';
import { isString } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import useVisitedMaze from '../../hooks/useVisitedMaze';
import { getImageAtSize } from '../../lib/utils';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import Loading from '../partials/Loading';

import styles from '../../../styles/questions/roads.module.scss';

interface RoadsQuestionProps {
    question: RoadsQuestion | null;
    answer?: ParticipationAnswer | null;
    game: RoadsGame | null;
    loading?: boolean;
    onAnswer: (string) => void;
    className?: string | null;
}

function RoadsQuestion({
    question = null,
    answer = null,
    game = null,
    loading = true,
    onAnswer = null,
    className = null,
}: RoadsQuestionProps) {
    const { value = null } = answer || {};
    const answers = useMemo(
        () =>
            value !== null && isString(value) && value.length > 0 ? value.split('-') || null : null,
        [value],
    );

    const { startImage = null, finishImage = null } = game || {};

    const intl = useIntl();
    const themeClassName = useThemeClassName(styles);

    const mediumStartImage = getImageAtSize(startImage, 'medium');
    const { url: startImageUrl = null } = mediumStartImage || {};

    const mediumFinishImage = getImageAtSize(finishImage, 'medium');
    const { url: finishImageUrl = null } = mediumFinishImage || {};

    const { choices = [], pattern = null, rows = 4, cols = 4 } = question || {};
    const finalPattern = pattern !== null ? pattern.split('-') : [];

    const values = answers !== null && answers.length > 0 ? answers : finalPattern;

    const initialItems = useMemo(
        () =>
            values.map((code) => ({
                id: code,
                ...choices.find(({ value = null }) => value === code),
            })),
        [values, choices],
    );

    const visited = useVisitedMaze(values || []);

    const [currentChoices, setCurrentChoices] = useState<RoadsChoice[]>(initialItems);

    const iterateChoice = (choice: RoadsChoice): RoadsChoice => {
        const { value = null, angle = 0 } = choice || {};

        if (value === null) {
            return choice;
        }

        const valueArray = value.split('_');

        const updatedValue = [
            valueArray[valueArray.length - 1],
            ...valueArray.slice(0, valueArray.length - 1),
        ].join('_');

        const updatedAngle = angle === 270 ? 0 : angle + 90;

        return {
            ...choice,
            value: updatedValue,
            angle: updatedAngle,
        };
    };

    const onClickChoice = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
            e.preventDefault();
            const clickedChoiceIndex = index || 0;
            setCurrentChoices((stateChoices) =>
                clickedChoiceIndex !== -1
                    ? [
                          ...stateChoices.slice(0, clickedChoiceIndex),
                          iterateChoice(stateChoices[clickedChoiceIndex]),
                          ...stateChoices.slice(clickedChoiceIndex + 1),
                      ]
                    : stateChoices,
            );
        },
        [choices, currentChoices, setCurrentChoices, onAnswer],
    );

    useEffect(() => {
        if (onAnswer !== null) {
            onAnswer(currentChoices.map(({ value }) => value).join('-'));
        }
    }, [currentChoices]);

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            <div className={styles.inner}>
                <div className={styles.top}>
                    <div className={styles.start}>
                        {startImageUrl !== null ? (
                            <img
                                src={startImageUrl}
                                alt={intl.formatMessage({
                                    defaultMessage: 'Start',
                                    description: 'Game label',
                                })}
                                className={styles.startImage}
                            />
                        ) : null}
                        <span className={styles.startLabel}>
                            <FormattedMessage defaultMessage="Start" description="Game label" />
                        </span>
                    </div>
                </div>
                <div
                    className={styles.grid}
                    style={{
                        gridTemplateColumns: `repeat(${cols}, 1fr)`,
                        gridTemplateRows: `repeat(${rows}, 1fr)`,
                    }}
                >
                    {(currentChoices || []).map(
                        ({ id: choiceId, image, activeImage = null, angle = 0 }, index) => {
                            const isVisited = visited !== null && visited[index] === true;
                            const mediumImage = getImageAtSize(
                                isVisited ? activeImage : image,
                                'medium',
                            );
                            const { url: mediumImageUrl = null } = mediumImage || {};

                            return (
                                <div key={`${choiceId}-${index + 1}`} className={styles.choice}>
                                    <Button
                                        onClick={(e) => onClickChoice(e, index)}
                                        className={styles.choiceButton}
                                    >
                                        <img
                                            src={mediumImageUrl}
                                            alt="tile"
                                            draggable={false}
                                            className={styles.image}
                                            style={{ transform: `rotate(${angle}deg)` }}
                                        />
                                    </Button>
                                </div>
                            );
                        },
                    )}
                </div>
                <div className={styles.bottom}>
                    <div className={styles.finish}>
                        {finishImageUrl !== null ? (
                            <img
                                src={finishImageUrl}
                                alt={intl.formatMessage({
                                    defaultMessage: 'Finish',
                                    description: 'Game label',
                                })}
                                className={styles.finishImage}
                            />
                        ) : null}
                        <span className={styles.finishLabel}>
                            <FormattedMessage defaultMessage="Finish" description="Game label" />
                        </span>
                    </div>
                </div>
                {loading ? <Loading loading={loading} className={styles.loading} /> : null}
            </div>
        </div>
    );
}

export default RoadsQuestion;
